<template>
  <ArrowTitle title="Программы" :is-app="isApp"></ArrowTitle>
  <SectionWrapper stretched>
    <div class="programs">
      <ProgramForm
        v-if="!selectedPrograms"
        :programsSetup="programsSetup"
        :programsSetupError="programsSetupError"
        @submitSelectedProgramGroups="prepareCheckup"
        @submitProgramsRegularCheckup="programsRegularCheckup = $event"
        @submitProgramsPersonalCheckup="programsPersonalCheckup = $event"
        @updateUserData="updateUserData"
        @openProgramGroupsInfo="handleOpenProgramGroupsInfo"
      />

      <ProgramSignup
        v-else
        :selectedPrograms="selectedPrograms"
        :genderId="genderId"
        :age="age"
        :filialId="filialId"
        :programsSetup="programsSetup"
        :programsRegularCheckup="programsRegularCheckup"
        :programsPersonalCheckup="programsPersonalCheckup"
        @updateSelectedPrograms="prepareCheckup"
        @updateProgramGroup="updateProgramGroup"
        @signUpForCheckup="sendScheduleRequest"
        @openProgramGroupsInfo="handleOpenProgramGroupsInfo"
        @reset="reset"
      />

      <ModalProgramGroupInfo
        :typeOfCheckupOpened="typeOfCheckupOpened"
        :title="modalTitle"
        :list="modalList"
        @closeModal="typeOfCheckupOpened = ''"
      />

      <ModalUserData
        :isModalOpen="isModalOpen"
        :loading="loading"
        :formError="formError"
        @closeModal="isModalOpen = false"
        @sendScheduleRequest="sendScheduleRequest"
      />

      <ModalSuccess
        :signUpSuccessMsg="signUpSuccessMsg"
        @closeSuccessModal="signUpSuccessMsg = ''"
      />
    </div>
  </SectionWrapper>
</template>

<script>
import { onMounted, ref, watchEffect, watch } from 'vue'
import ProgramForm from '@/views/programs/checkup/components/ProgramForm.vue'
import ProgramSignup from '@/views/programs/checkup/components/ProgramSignup.vue'
import ModalUserData from '@/views/programs/checkup/components/ModalUserData.vue'
import { postProgramsCheckup } from '@/views/programs/checkup/api/postProgramsCheckup.js'
import ModalSuccess from '@/views/programs/checkup/components/ModalSuccess.vue'
import { getProgramsSetup } from '@/views/programs/checkup/api/getProgramsSetup.js'
import ModalProgramGroupInfo from '@/views/programs/checkup/components/ModalProgramGroupInfo.vue'
import ArrowTitle from '@/components/ui/typography/ArrowTitle.vue'
import SectionWrapper from '@/components/ui/SectionWrapper.vue'
import { useUserStore } from '@/stores/UserStore.js'

export default {
  name: 'App',
  components: {
    ProgramForm,
    ProgramSignup,
    ModalUserData,
    ModalSuccess,
    ModalProgramGroupInfo,
    ArrowTitle,
    SectionWrapper
  },
  setup () {
    const programsRegularCheckup = ref(null)
    const programsPersonalCheckup = ref(null)
    const programsSetup = ref(null)
    const programsSetupError = ref(null)
    const selectedPrograms = ref(null)
    const isModalOpen = ref(false)
    const typeOfCheckupOpened = ref('')
    const formError = ref(null)
    const modalTitle = ref('')
    const modalList = ref([])
    const genderId = ref(null)
    const age = ref(null)
    const typeId = ref(null)
    const filialId = ref(null)
    const signUpSuccessMsg = ref('')
    const loading = ref(false)

    const userStore = useUserStore()

    const updateUserData = (userData) => {
      genderId.value = userData.genderId
      age.value = userData.age
      typeId.value = userData.typeId
      filialId.value = userData.filialId
    }

    const prepareUniquePrograms = (programGroups) => {
      const codesSet = new Set()
      return programGroups.map((pg) => {
        const uniqueItems = pg.items.map((i) => {
          if (!codesSet.has(i.code)) {
            codesSet.add(i.code)
            return { ...i, isSelected: true, isCrossed: false }
          }
          return { ...i, isSelected: false, isCrossed: true }
        })

        return { ...pg, items: uniqueItems }
      })
    }

    const prepareCheckup = (submittedProgramGroups) => {
      if (submittedProgramGroups.length) {
        selectedPrograms.value = prepareUniquePrograms(submittedProgramGroups)
      }
    }
    const updateProgramGroup = (newProgramsData) => {
      const updated = []

      selectedPrograms.value.forEach((program) => {
        const match = newProgramsData.find((pg) => pg.id === program.id)

        if (!match) {
          updated.push(program)
        } else if (match.isSelected) {
          updated.push(match)
        }
      })

      newProgramsData.forEach((pg) => {
        const alreadyIncluded = updated.some((p) => p.id === pg.id)
        if (pg.isSelected && !alreadyIncluded) {
          updated.push(pg)
        }
      })

      selectedPrograms.value = updated
      prepareCheckup(updated)
    }

    function prepareData (checkup) {
      return checkup.map((group) => ({
        group_id: group.id,
        id: group.items?.map((item) => item.id) ?? []
      }))
    }

    const sendScheduleRequest = async (userData) => {
      const dataToSend = prepareData(selectedPrograms.value)
      // console.log('dataToSend', dataToSend);
      try {
        loading.value = true
        const data = await postProgramsCheckup({
          name: userStore?.userFullName,
          phone: userStore?.user?.username,
          filial_id: filialId.value,
          gender_id: genderId.value,
          type_id: 1,
          age: age.value,
          checkup: dataToSend
        })
        reset()
        signUpSuccessMsg.value = data.message
      } catch (err) {
        formError.value = err
        signUpSuccessMsg.value = ''
      } finally {
        loading.value = false
      }
    }

    const openModal = () => {
      isModalOpen.value = true
    }

    const reset = () => {
      window.scrollTo({
        top: 0
      })
      selectedPrograms.value = null
      isModalOpen.value = false
      formError.value = null
      signUpSuccessMsg.value = ''

      genderId.value = null
      age.value = null
      typeId.value = null
      filialId.value = null
    }

    watchEffect(() => {
      // console.log('userStore', userStore?.user)
    })

    watch(
      () => typeOfCheckupOpened.value,
      (newValue) => {
        document.body.style.overflow = newValue ? 'hidden' : ''
      }
    )

    watch(
      () => isModalOpen.value,
      (newValue) => {
        document.body.style.overflow = newValue ? 'hidden' : ''
      }
    )

    watch(
      () => signUpSuccessMsg.value,
      (newValue) => {
        document.body.style.overflow = newValue ? 'hidden' : ''
      }
    )

    onMounted(async () => {
      try {
        programsSetup.value = await getProgramsSetup()
        // console.log(programsSetup.value)
      } catch (err) {
        programsSetupError.value = err
      }
    })

    function handleOpenProgramGroupsInfo (type, title, list) {
      typeOfCheckupOpened.value = type
      modalTitle.value = title
      modalList.value = list
    }

    return {
      openModal,
      updateUserData,
      prepareCheckup,
      sendScheduleRequest,
      reset,
      updateProgramGroup,
      programsSetupError,
      signUpSuccessMsg,
      selectedPrograms,
      typeOfCheckupOpened,
      formError,
      isModalOpen,
      programsSetup,
      genderId,
      age,
      filialId,
      programsRegularCheckup,
      programsPersonalCheckup,
      handleOpenProgramGroupsInfo,
      modalTitle,
      modalList,
      loading,
      userStore
    }
  }
}
</script>

<style scoped lang="scss">
.programs {
  min-height: 800px;
}
</style>
